import React from 'react';

import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../graphql-fragments/SanityImage';
import { getUrlFromVersatileLink } from '../../utils/sanity';
import { clsx, replaceNewLinesWithBr } from '../../utils/utils';
import * as styles from './Card.module.scss';
import Image from './Image';
import SmartLink from './SmartLink';
import TextLink from './TextLink';

export interface CardProps {
  image: SanityImageType;
  title: string;
  superTitle?: string;
  subtitle?: string;
  text?: string;
  link?: ButtonLinkType;
  withTransparentBackground?: boolean;
  layout?: 'vertical' | 'horizontal';
  imageAspectRatio?: 'square' | 'rectangle';
  withSmallerFontSize?: boolean;
  withImageAsBackground?: boolean;
  withRoundedCorner?: boolean;
  withColoredBackground?: boolean;
  className?: string;
}

const Card = ({
  image,
  superTitle,
  title,
  subtitle,
  text,
  link,
  withTransparentBackground,
  layout,
  imageAspectRatio,
  withSmallerFontSize,
  withImageAsBackground,
  withRoundedCorner,
  withColoredBackground,
  className,
}: CardProps): React.ReactElement => {
  function renderCard(image: SanityImageType, title: string, text?: string, link?: ButtonLinkType) {
    return (
      <div
        className={clsx(
          styles.container,
          withTransparentBackground && styles.withTransparentBackground,
          layout === 'horizontal' && styles.horizontalLayout,
          withImageAsBackground && styles.withImageAsBackground,
          withRoundedCorner && styles.withRoundedCorner,
          withColoredBackground && styles.withColoredBackground,
          className,
        )}
      >
        {withImageAsBackground && (
          <div className={styles.backgroundImage}>
            <Image image={image} />
            <div className={styles.overlay}></div>
          </div>
        )}
        {!withImageAsBackground && (
          <div
            className={clsx(
              styles.imageContainer,
              imageAspectRatio === 'square' && styles.squareImage,
            )}
          >
            <Image image={image} />
          </div>
        )}
        <div className={clsx(styles.textContainer, withSmallerFontSize && styles.smallerFontSize)}>
          {superTitle && <span className={styles.superTitle}>{superTitle}</span>}
          <h3 className={clsx(styles.title, !text && styles.withMarginBottom)}>
            {replaceNewLinesWithBr(title)}
          </h3>
          {text && <p className={styles.text}>{text}</p>}
          {link && (
            <TextLink styleOnly withArrowIcon className={styles.link}>
              {link.text}
            </TextLink>
          )}
        </div>
      </div>
    );
  }
  return link ? (
    <SmartLink to={getUrlFromVersatileLink(link)} className={clsx(className, styles.cardLink)}>
      {renderCard(image, title, text, link)}
    </SmartLink>
  ) : (
    <div className={className}>{renderCard(image, title, text)}</div>
  );
};

export default Card;
